import { RightOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { motion } from 'sutra/tokens/motion'

import GenericSvgIcon from 'components/GenericSvgIcon'
import {
  NavIconWrapper,
  NavText,
  navRowCss,
} from 'components/navigation/nav-row-styles'

type NavGroupProps = {
  title: string
  svgComponent: React.FC
  isExpanded: boolean
  isCollapsed: boolean
  onToggle: () => void
  children: React.ReactNode
}

// The group header button plus its children. Height/geometry come from the
// same `navRowCss` a NavItemLink uses, so the header is pixel-identical to a
// sibling row. The group's own expand/collapse state (`isExpanded`) always
// wins, in both sidebar modes: collapsing the group hides its children
// whether the sidebar is expanded or icon-collapsed, and toggling the
// sidebar itself never changes which children are visible — only their
// width/indent. That's also why children always render through the same
// ChildrenViewport/ChildrenSlide wrapper regardless of `isCollapsed`: the
// DOM shape must stay identical across a sidebar toggle so each NavItemLink
// simply re-styles (and transitions) instead of unmounting and popping back
// in.
export const NavGroup: React.FC<NavGroupProps> = ({
  title,
  svgComponent,
  isExpanded,
  isCollapsed,
  onToggle,
  children,
}) => {
  return (
    <>
      <Tooltip content={isCollapsed ? title : ''} side="right">
        <HeaderButton
          type="button"
          aria-expanded={isExpanded}
          $isActive={false}
          $isCollapsed={isCollapsed}
          onClick={onToggle}
        >
          <NavIconWrapper>
            <GenericSvgIcon SvgComponent={svgComponent} />
          </NavIconWrapper>
          {/* Always mounted — $hidden fades instead of unmounting, same
              reasoning as NavItemLink's label. */}
          <NavText $hidden={isCollapsed}>{title}</NavText>
          <Chevron $expanded={isExpanded} $hidden={isCollapsed}>
            <RightOutlined />
          </Chevron>
        </HeaderButton>
      </Tooltip>
      <ChildrenViewport $expanded={isExpanded}>
        <ChildrenSlide
          $expanded={isExpanded}
          $isCollapsed={isCollapsed}
          aria-hidden={!isExpanded}
        >
          {children}
        </ChildrenSlide>
      </ChildrenViewport>
    </>
  )
}

const HeaderButton = styled.button<{
  $isActive: boolean
  $isCollapsed: boolean
}>`
  ${navRowCss}
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '0 6px' : '0 6px')};
`

// $hidden's opacity fade is intentionally NOT under the reduced-motion guard
// below: it's a layout-state fade that matches navRowCss's own (ungated)
// width/padding/background transitions on the same row, not a standalone
// motion effect like the rotate transition is.
const Chevron = styled.span<{ $expanded: boolean; $hidden?: boolean }>`
  display: flex;
  align-items: center;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: no-preference) {
    transition:
      transform ${motion.duration.base}ms ${motion.ease},
      opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  svg {
    font-size: 10px;
  }
`

// The grid-rows trick: animating a 0fr/1fr row lets the child block grow to
// its natural height without measuring it in JS. `ChildrenSlide` is the
// single grid item — `overflow: hidden` + `min-height: 0` are what let the
// 0fr row actually collapse to zero (a grid item's default min-height is
// auto, which would otherwise clamp the row to the content's height). Same
// element also carries the slide/fade, so height and slide animate together.
//
// `width: 100%` + `grid-template-columns: minmax(0, 1fr)`: this is a direct
// child of NavigationList, a `display: flex; align-items: flex-start` column
// — children don't stretch to fill the cross-axis by default, and a `grid`
// box with no column track sizes to its content's max-content width either.
// Without both, this (and everything inside it) shrinks to fit its content
// instead of spanning the same width as every top-level row.
const ChildrenViewport = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  width: 100%;

  @media (prefers-reduced-motion: no-preference) {
    transition: grid-template-rows ${motion.duration.base}ms ${motion.ease};
  }
`

// Negative Y (not X): the header sits above the children, so the collapsed
// resting position is shifted up toward/behind the header — children slide
// down and out from underneath it on expand, and back up under it on
// collapse.
//
// The indent lives here, not on each row: `padding-left` (with box-sizing:
// border-box) insets the left edge only, so a nested NavItemLink's own
// background starts at this boundary — 6px before its icon, same relationship
// every top-level row's highlight already has to its own icon — instead of
// starting at x:0 and covering the indent gutter. The right edge is
// untouched, so nested rows still reach the same right edge as every
// top-level row. 14px here + each row's own 6px base padding = 20px total
// from the sidebar edge to the icon, the same indent as before this fix.
//
// The indent only applies when the SIDEBAR is expanded ($isCollapsed false).
// When the sidebar itself is icon-collapsed, every nested row already
// renders at a fixed 32px width (navRowCss); indenting that fixed-width box
// pushes it past the sidebar's narrow collapsed column and its own
// `overflow: hidden` clips the overflow — the "cut off" icon bug. Zeroing
// the indent in collapsed mode makes nested icons sit flush at the same x
// position as every top-level icon, same as the reference screenshot.
const ChildrenSlide = styled.div<{ $expanded: boolean; $isCollapsed: boolean }>`
  box-sizing: border-box;
  overflow: hidden;
  min-height: 0;
  padding-left: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '14px')};
  display: flex;
  flex-direction: column;
  gap: 2px; /* matches NavigationList's own gap between top-level rows */
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transform: translateY(${({ $expanded }) => ($expanded ? '0' : '-8px')});
  transition: padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: no-preference) {
    transition:
      padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      opacity ${motion.duration.base}ms ${motion.ease},
      transform ${motion.duration.base}ms ${motion.ease};
  }
`
